import { generatePath } from 'react-router-dom';

import { GetAllSites } from '@/api/fe-api/view/queries/get-all-sites';
import { DropdownItem } from '@/components/ui/dropdown';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import type { TaskDetails } from '@/task-manager/entities/task-details';
import type { TaskDevice } from '@/task-manager/entities/task-device';

export type GoToRecommendationsItemProps = {
  task: TaskDetails;
};

function generateSearchParams(device: TaskDevice): URLSearchParams {
  const searchParams = new URLSearchParams();

  if (!device.level || !device.fullFrontendId) {
    return searchParams;
  }

  searchParams.set(`${device.level}_id`, device.fullFrontendId);

  return searchParams;
}

export function GoToRecommendationsItem({
  task,
}: GoToRecommendationsItemProps) {
  const { config } = useMe();
  const sites = GetAllSites.useQuery({ enabled: config.type !== 'mobility' });
  const featureFlag = useFeatureFlag();

  if (
    !task.device ||
    !featureFlag('details') ||
    Boolean(task.projectManagement?.workstream.id)
  ) {
    return null;
  }

  if (config.type === 'mobility') {
    return (
      <DropdownItem
        to={generatePath('/vehicle/:vehicleId/details', {
          vehicleId: task.device.topFrontendId ?? task.title,
        })}
      >
        Go to alert details
      </DropdownItem>
    );
  }

  const relatedAsset = sites.data?.find(
    ({ fullFrontendId }) => fullFrontendId === task.device?.groupFrontendId,
  );

  if (!relatedAsset) {
    // TODO: handle the case where task.device.groupFrontendId is set to redirect to another page
    return null;
  }

  const searchParams = generateSearchParams(task.device);

  return (
    <DropdownItem
      data-testid="go-to-details"
      to={{
        pathname: generatePath('/asset/:assetId/details', {
          assetId: relatedAsset?.id,
        }),
        search: searchParams.toString(),
      }}
    >
      {task.alert ? 'Go to alert details' : 'Go to recommendations'}
    </DropdownItem>
  );
}
